// referenced from confluence/next/packages/error-boundary/src/DevErrorDetail.tsx

import React, { type ErrorInfo, Fragment, useState } from 'react';

import Link from '@atlaskit/link';
import { Text } from '@atlaskit/primitives/compiled';
import { WidthObserver } from '@atlaskit/width-detector';

import {
	getLogSafeErrorAttributes,
	getReactErrorAttributes,
} from '../../../utils/error/error-utils';

// If any changes are made here, they should also be made in that file.
const getUrlForQuery = (query: string): string =>
	`https://splunk.paas-inf.net/en-US/app/search/search?q=search%20${encodeURIComponent(
		query,
	)}&display.page.search.mode=smart`;

type ErrorComponentProps = {
	error: Error;
	errorInfo?: ErrorInfo;
};

export function DevErrorDetail({ error, errorInfo }: ErrorComponentProps): React.JSX.Element {
	const reactErrorAttributes = getReactErrorAttributes(error);
	const errorHashCode = reactErrorAttributes ? reactErrorAttributes.errorHashCode : undefined;
	const traceId = getLogSafeErrorAttributes(error).traceId;

	let splunkQueryText: string | undefined;
	let splunkQueryHref: string | undefined;
	if (errorHashCode) {
		splunkQueryText = `ext.event.attributes.errorHashCode="${errorHashCode}"`;
		splunkQueryHref = getUrlForQuery(
			[
				'eventtype="micros_analytics-service"',
				'ext.event.product=confluence',
				splunkQueryText,
			].join('\n'),
		);
	}

	const [width, setWidth] = useState(0);

	return (
		<Fragment>
			<WidthObserver setWidth={setWidth} />
			<Text>
				You can also discuss this in the{' '}
				<Link target="_blank" href="https://atlassian.slack.com/messages/CFG8QANL9">
					#help-search-plex
				</Link>{' '}
				Slack channel.
			</Text>
			{splunkQueryText && splunkQueryHref ? (
				<Text>
					You can search{' '}
					<Link href={splunkQueryHref}>
						<Text>Splunk</Text>
					</Link>{' '}
					for <b>{splunkQueryText}</b>. g{' '}
				</Text>
			) : null}
			{traceId ? (
				<Text>
					Trace ID: <b>{traceId}</b>
				</Text>
			) : null}
			<Text>
				Error:
				{width ? (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div style={{ overflow: 'scroll', width }}>
						<pre>
							{`${error.message || String(error)}`}
							{errorInfo && `${errorInfo.componentStack}`}
						</pre>
					</div>
				) : null}
			</Text>
		</Fragment>
	);
}
